import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission, useFileUpload } from '../../../hooks';
import {
  UploadContainer,
  UploadTitle,
  UploadButton,
  ChooseFileButton,
  MainContent
} from './eNO1Upload.styled';
import { ErrorTable } from '../../../common/components/ErrorTable';
import { isPlaygroundFile } from '../../../utils/filenameUtils';

// Define metadata interface
interface FileMetadata {
  fileId: string | null;
  fileName: string;
  operatorByFileUploader: string;
  operatorByInboundPpo: string;
  operatorByOutboundPpo: string;
  accountingYear: number;
  accountingQuarter: number;
  fileVersion: number;
  creationDate: string;
  inboundRemovalDate: string | null;
  outboundRemovalDate: string | null;
  fileStatus: string;
  processDate: string | null;
  inboundAcceptanceDate: string | null;
  outboundAcceptanceDate: string | null;
  notifiedDate: string | null;
  downloadDate: string | null;
  validStatus: string | null;
  samplesCount: number | null;
  pendingOn: string;
  loggedUserOperator: string;
  deletedAt: string | null;
  comment: string | null;
  samples: any | null;
}

const ENO1Upload: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [successNotification, setSuccessNotification] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading } = usePermission('/eno1', '/upload');
  
  // Use the file upload hook
  const { 
    selectedFile,
    isUploading,
    uploadError,
    validationErrors,
    handleFileChange,
    handleUpload,
    uploadToEndpoint,
    resetUpload
  } = useFileUpload({
    onUploadSuccess: (response: any) => {
      console.log('Upload success response:', response);
      
      // Store metadata from response
      if (response && response.metadata) {
        setFileMetadata(response.metadata);
      }
      
      // Mark that initial file upload was successful
      setFileUploaded(true);
    },
    allowedFileTypes: ['.csv'], // Only allow CSV files
    maxFileSizeMB: 5, // 5MB max file size
    uploadEndpoint: '/data-set/eno1' // Using relative path with existing axios config
  });
  
  // Handle final upload
  const handleFinalUpload = useCallback(async () => {
    try {
      const result = await uploadToEndpoint('/data-set/eno1/record?isFinal=true');
      if (result) {
        setUploadMessage('File has been successfully uploaded as Final!');
        navigate(`/eno1`);
        setSuccessNotification(true);
        setFileUploaded(false);
        setFileMetadata(null);
        // Completely reset the file selection
        resetUpload();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error uploading as Final:', error);
    }
  }, [uploadToEndpoint, resetUpload]);
  
  // Handle draft upload
  const handleDraftUpload = useCallback(async () => {
    try {
      const result = await uploadToEndpoint('/data-set/eno1/record?isFinal=false');
      if (result) {
        setUploadMessage('File has been successfully uploaded as Draft!');
        setSuccessNotification(true);
        
        // Extract fileId from the response for redirection
        if (result.metadata && result.metadata.fileId) {
          // Navigate to the samples messages page with the fileId
          navigate(`/eno1/messages/samples/${result.metadata.fileId}?editMode=true`);
        } else {
          // Reset states if no fileId is available
          setFileUploaded(false);
          setFileMetadata(null);
          // Completely reset the file selection
          resetUpload();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error uploading as Draft:', error);
    }
  }, [uploadToEndpoint, resetUpload, navigate]);
  
  // Handle Cancel action
  const handleCancelUpload = useCallback(() => {
    resetUpload();
    setFileUploaded(false);
    setFileMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetUpload]);
  
  // Get all errors to display in the table
  const allErrors = React.useMemo(() => {
    const errors = [...validationErrors];
    
    // Add uploadError to the errors list if it exists
    if (uploadError) {
      // Convert generic error to ValidationError format
      if (!validationErrors.length) {
        // Only add general error if there are no specific validation errors
        errors.push({
          sampleId: null,
          field: 'General',
          errorCode: uploadError.code || 'ERROR',
          message: uploadError.message,
          version: null
        });
        
        // Add any details as separate error entries
        if (uploadError.details && uploadError.details.length > 0) {
          uploadError.details.forEach(detail => {
            errors.push({
              sampleId: null,
              field: 'Detail',
              errorCode: 'INFO',
              message: detail,
              version: null
            });
          });
        }
        
        // Add validationErrors from uploadError if they exist
        if (uploadError.validationErrors && uploadError.validationErrors.length > 0) {
          errors.push(...uploadError.validationErrors);
        }
      }
    }
    
    return errors;
  }, [uploadError, validationErrors]);
  
  // Handle notification close
  const handleNotificationClose = useCallback(() => {
    setSuccessNotification(false);
    // Reset the upload state when the notification is closed
    resetUpload();
    // Ensure file input is reset
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetUpload]);

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  // Handle file input click
  const handleChooseFileClick = () => {
    // Reset the file input before clicking it to ensure it works every time
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // Initial file upload handler
  const handleInitialUpload = async () => {
    try {
      await handleUpload();
      // The fileUploaded state will be set by the onUploadSuccess callback
    } catch (error) {
      // Error handling is done by the useFileUpload hook
      console.error('Error in initial upload:', error);
    }
  };

  // Reset file input when selectedFile becomes null
  useEffect(() => {
    if (!selectedFile && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFile]);

  // Detect whether the validated dataset is a playground file
  const isPlaygroundDataset = React.useMemo(
    () => (fileMetadata ? isPlaygroundFile(fileMetadata) : false),
    [fileMetadata]
  );

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} data-testid="eno1-upload-loading">
        <CircularProgress data-testid="eno1-upload-loading-spinner" />
      </Box>
    );
  }

  // Show unauthorized message if the user doesn't have permission
  if (!hasPermission) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} data-testid="eno1-upload-unauthorized">
        <Typography variant="h6" color="error" data-testid="eno1-upload-unauthorized-message">
          You don't have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="eno1-upload-container">
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title="eNO1 Upload"
            subtitle="eNO1 Sampling"
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{}}
            data-testid="eno1-upload-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          <Sidebar 
            isOpen={sidebarOpen} 
            onToggle={handleSidebarToggle}
            navItems={navItems}
            data-testid="eno1-upload-sidebar"
          />

          {/* MainContent */}
          <MainContent data-testid="eno1-upload-main-content">
            <UploadContainer data-testid="eno1-upload-container-inner">
              <UploadTitle data-testid="eno1-upload-title">eNO1 Upload</UploadTitle>
              
              {/* Initial file selection UI */}
              {!fileUploaded && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} data-testid="eno1-upload-initial-ui">
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2
                  }} data-testid="eno1-upload-button-group">
                    <input
                      type="file"
                      id="eno1-file-upload"
                      ref={fileInputRef}
                      hidden
                      onChange={handleFileChange}
                      accept=".csv"
                      disabled={isUploading}
                      data-testid="eno1-upload-file-input"
                    />
                    <ChooseFileButton
                      variant="outlined"
                      disabled={isUploading}
                      onClick={handleChooseFileClick}
                      data-testid="eno1-upload-choose-file-button"
                    >
                      Choose CSV File
                    </ChooseFileButton>
                    
                    <UploadButton
                      variant="contained"
                      onClick={handleInitialUpload}
                      disabled={!selectedFile || isUploading}
                      data-testid="eno1-upload-initial-upload-button"
                    >
                      {isUploading ? 'UPLOADING...' : 'UPLOAD'}
                    </UploadButton>
                  </Box>
                  
                  {!selectedFile && !isUploading && (
                    <Typography sx={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }} data-testid="eno1-upload-instruction-text">
                      Please select a CSV file first. The Upload button will be enabled after file selection.
                    </Typography>
                  )}
                  
                  {selectedFile && !isUploading && allErrors.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="eno1-upload-selected-file-info">
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, mr: 1 }} data-testid="eno1-upload-selected-file-label">
                        Selected file:
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#00B6BA' }} data-testid="eno1-upload-selected-file-name">
                        {selectedFile.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Final/Draft upload UI - shown after initial upload */}
              {fileUploaded && selectedFile && !isUploading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} data-testid="eno1-upload-confirmation-ui">
                  <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="eno1-upload-confirmation-file-info">
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mr: 1 }} data-testid="eno1-upload-confirmation-file-label">
                      Ready to upload file:
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#00B6BA', fontWeight: 'bold' }} data-testid="eno1-upload-confirmation-file-name">
                      {selectedFile.name}
                    </Typography>
                  </Box>
                  {fileMetadata && (
                    <Box sx={{ 
                      backgroundColor: '#f5f9fa', 
                      p: 2, 
                      borderRadius: 1, 
                      border: '1px solid #e0f2f1',
                      mb: 2
                    }} data-testid="eno1-upload-file-metadata">
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }} data-testid="eno1-upload-validation-success-text">
                        File Validated Successfully
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} data-testid="eno1-upload-metadata-column-1">
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-filename">
                            <strong>File Name:</strong> {fileMetadata.fileName}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-status">
                            <strong>Status:</strong> {fileMetadata.fileStatus}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-creation-date">
                            <strong>Created:</strong> {new Date(fileMetadata.creationDate).toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-testid="eno1-upload-metadata-column-2">
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-year-quarter">
                            <strong>Year/Quarter:</strong> {fileMetadata.accountingYear} / Q{fileMetadata.accountingQuarter}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-version">
                            <strong>Version:</strong> {fileMetadata.fileVersion}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#555' }} data-testid="eno1-upload-metadata-pending">
                            <strong>Pending on:</strong> {fileMetadata.pendingOn}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  <Typography sx={{ fontSize: '14px' }} data-testid="eno1-upload-choose-upload-type-text">
                    Please choose how to upload this file:
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    mt: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                  }} data-testid="eno1-upload-actions">
                    {!isPlaygroundDataset && (
                      <UploadButton
                        variant="contained"
                        onClick={handleFinalUpload}
                        color="primary"
                        disabled={isUploading}
                        sx={{
                          flex: { xs: '1 1 auto', sm: '0 1 auto' },
                          backgroundColor: '#00B6BA',
                          '&:hover': {
                            backgroundColor: '#009B9F',
                          },
                        }}
                        data-testid="eno1-upload-final-button"
                      >
                        {isUploading ? 'UPLOADING...' : 'Upload as Final'}
                      </UploadButton>
                    )}

                    <UploadButton
                      variant="contained"
                      onClick={handleDraftUpload}
                      color="secondary"
                      disabled={isUploading || (fileMetadata?.fileVersion === 0 && !isPlaygroundDataset)}
                      sx={{
                        flex: { xs: '1 1 auto', sm: '0 1 auto' },
                        backgroundColor: '#6c757d',
                        '&:hover': {
                          backgroundColor: '#5a6268',
                        },
                      }}
                      data-testid="eno1-upload-draft-button"
                    >
                      {isUploading ? 'UPLOADING...' : 'Upload as Draft'}
                    </UploadButton>

                    <ChooseFileButton
                      variant="outlined"
                      onClick={handleCancelUpload}
                      disabled={isUploading}
                      sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                      data-testid="eno1-upload-cancel-button"
                    >
                      Cancel
                    </ChooseFileButton>
                  </Box>
                </Box>
              )}

              {isUploading && (
                <Box sx={{
                  width: '100%',
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }} data-testid="eno1-upload-loading-indicator">
                  <CircularProgress
                    size={40}
                    sx={{ color: '#00B6BA' }}
                    data-testid="eno1-upload-progress"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }} data-testid="eno1-upload-loading-text">
                    Uploading file... Please wait
                  </Typography>
                </Box>
              )}

              {/* Show ALL errors in the table */}
              {allErrors.length > 0 && (
                <ErrorTable
                  errors={allErrors}
                  fileName={selectedFile?.name}
                  data-testid="eno1-upload-error-table"
                />
              )}
            </UploadContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Success notification */}
      <NotificationSnackbar
        open={successNotification}
        message={uploadMessage || 'File has been successfully uploaded!'}
        severity="success"
        onClose={handleNotificationClose}
        data-testid="eno1-upload-notification"
      />
    </Box>
  );
};

export default ENO1Upload;

